import { FormEvent, useEffect, useState } from "react";
import { api, TotpSetupResponse } from "../lib/api";
import { useAuth } from "../lib/auth";

interface Props {
  onClose: () => void;
}

type Tab = "profile" | "security";

export default function ProfileModal({ onClose }: Props) {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState<Tab>("profile");

  // Profile fields
  const [displayName, setDisplayName] = useState(user?.display_name ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Security / 2FA state
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(user?.display_name ?? "");
  }, [user?.display_name]);

  // If the cached user is missing fields the modal needs (e.g. email is absent
  // for sessions that logged in before the field was persisted), pull a fresh
  // copy from the server so the form populates.
  useEffect(() => {
    if (user && !user.email) {
      void refreshUser();
    }
  }, [user, refreshUser]);

  function clearStatus() {
    setErr(null);
    setMsg(null);
  }

  async function onSaveProfile(ev: FormEvent) {
    ev.preventDefault();
    clearStatus();

    const wantsPasswordChange = newPassword.length > 0 || confirmPassword.length > 0;
    if (wantsPasswordChange) {
      if (newPassword.length < 8) {
        setErr("New password must be at least 8 characters.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setErr("New passwords don't match.");
        return;
      }
      if (!currentPassword) {
        setErr("Enter your current password to change it.");
        return;
      }
    }

    const body: Record<string, string> = {};
    if (displayName && displayName !== user?.display_name) body.display_name = displayName;
    if (wantsPasswordChange) {
      body.current_password = currentPassword;
      body.new_password = newPassword;
    }
    if (Object.keys(body).length === 0) {
      setMsg("Nothing to update.");
      return;
    }

    setBusy(true);
    try {
      await api.patch("/auth/me", body);
      await refreshUser();
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMsg("Profile updated.");
    } catch (e: any) {
      setErr(e?.message ?? "Failed to update profile.");
    } finally {
      setBusy(false);
    }
  }

  async function startTotpSetup() {
    clearStatus();
    setBusy(true);
    try {
      const resp = await api.post<TotpSetupResponse>("/auth/2fa/setup");
      setSetup(resp);
      setOtpCode("");
    } catch (e: any) {
      setErr(e?.message ?? "Couldn't start 2FA setup.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmTotp(ev: FormEvent) {
    ev.preventDefault();
    clearStatus();
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code: otpCode.trim() });
      await refreshUser();
      setSetup(null);
      setOtpCode("");
      setMsg("Two-factor authentication enabled.");
    } catch (e: any) {
      setErr(e?.message ?? "Invalid code.");
    } finally {
      setBusy(false);
    }
  }

  async function disableTotp(ev: FormEvent) {
    ev.preventDefault();
    clearStatus();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePassword });
      await refreshUser();
      setDisablePassword("");
      setMsg("Two-factor authentication disabled.");
    } catch (e: any) {
      setErr(e?.message ?? "Couldn't disable 2FA.");
    } finally {
      setBusy(false);
    }
  }

  const totpEnabled = !!user?.totp_enabled;

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal__card profile-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Account</h2>

        <div className="tabs tabs--inline">
          <button
            type="button"
            className={tab === "profile" ? "active" : ""}
            onClick={() => { clearStatus(); setTab("profile"); }}
          >
            Profile
          </button>
          <button
            type="button"
            className={tab === "security" ? "active" : ""}
            onClick={() => { clearStatus(); setTab("security"); }}
          >
            Security
          </button>
        </div>

        {err && <div className="alert alert--error">{err}</div>}
        {msg && <div className="alert alert--ok">{msg}</div>}

        {tab === "profile" && (
          <form onSubmit={onSaveProfile} className="profile-form">
            <label>
              <span>Email</span>
              <input value={user?.email ?? ""} disabled />
            </label>
            <label>
              <span>Display name</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </label>

            <h3 className="profile-form__section">Change password</h3>
            <label>
              <span>Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </label>
            <label>
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </label>
            <label>
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </label>

            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                Close
              </button>
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}

        {tab === "security" && (
          <div className="profile-form">
            <p className="muted">
              Two-factor authentication adds a one-time code from an
              authenticator app (Google Authenticator, 1Password, Authy, …) on
              top of your password.
            </p>

            {totpEnabled ? (
              <form onSubmit={disableTotp} className="profile-form">
                <div className="profile-status profile-status--on">
                  Two-factor authentication is <strong>enabled</strong>.
                </div>
                <label>
                  <span>Confirm password to disable</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    required
                  />
                </label>
                <div className="modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={onClose}>
                    Close
                  </button>
                  <button type="submit" className="btn" disabled={busy || !disablePassword}>
                    {busy ? "Working…" : "Disable 2FA"}
                  </button>
                </div>
              </form>
            ) : !setup ? (
              <>
                <div className="profile-status">
                  Two-factor authentication is <strong>off</strong>.
                </div>
                <div className="modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={onClose}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={startTotpSetup}
                    disabled={busy}
                  >
                    {busy ? "Working…" : "Enable 2FA"}
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={confirmTotp} className="profile-form">
                <p>Scan this QR code with your authenticator app:</p>
                <img
                  src={setup.qr_data_url}
                  alt="Scan with authenticator app"
                  className="profile-qr"
                />
                <p className="muted">
                  Or enter this secret manually:
                </p>
                <code className="profile-secret">{setup.secret}</code>
                <label>
                  <span>Enter 6-digit code from your app</span>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={8}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    required
                  />
                </label>
                <div className="modal__actions">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => { setSetup(null); clearStatus(); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={busy || otpCode.length < 6}
                  >
                    {busy ? "Verifying…" : "Verify & enable"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
